<template>
    <main class="p-5 w-full h-full flex flex-col items-start">
        <div class="flex w-full flex-grow flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="bg-white font-bold">
                    <tr>
                        <th style="width: 2rem" class="border-r p-1 text-center">
                            {{ $t('supports.code') }}
                        </th>
                        <th style="width: 2rem" class="border-r p-1 text-center">
                            {{ $t('supports.type') }}
                        </th>
                        <th style="width: 2rem" class="border-r p-1 text-center">
                            {{ $t('commons.version') }}
                        </th>
                        <th class="border-r p-1 text-center">
                            {{ $t('commons.name') }}
                        </th>
                        <th class="border-r p-1 text-center">
                            {{ $t('commons.locations') }}
                        </th>
                        <th style="width: auto" class="border-r p-1 text-center">
                            {{ $t('bundles.emitter') }}
                        </th>
                        <th style="width: 6rem" class="border-r p-1 text-center">
                            {{ $t('commons.emittedOn') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="support in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="border-r p-1">
                                {{ support.code }}
                            </td>
                            <td class="border-r p-1">
                                <span :title="$t('supports.types.text')" v-if="support.type === 'text'">
                                    <icon-file-document-outline width="16" height="16" />
                                </span>
                                <span :title="$t('supports.types.graphic')" v-else-if="support.type === 'graphic'">
                                    <icon-file-image-outline width="16" height="16" />
                                </span>
                                <span :title="$t('supports.types.other')" v-else>
                                    <icon-file-outline width="16" height="16" />
                                </span>
                            </td>
                            <td class="text-center border-r p-1">
                                {{ support.versions.length }}
                            </td>
                            <td class="border-r p-1">
                                <div class="flex justify-between items-center">
                                    {{ support.name }}
                                    <span v-if="!support.name || support.name.trim().length === 0">...</span>
                                </div>
                            </td>
                            <td class="border-r p-1">
                                <div v-if="support.locations.length === 0">...</div>
                                <div
                                    v-else-if="support.mergedLocations.length === 1"
                                    :title="support.locations.map((zone) => zone.fullName).join('\n')"
                                >
                                    {{ support.mergedLocations[0] }}
                                </div>
                                <div v-else :title="support.locations.map((zone) => zone.fullName).join('\n')">
                                    ({{ support.locationIds.length }})
                                </div>
                            </td>
                            <td class="text-left border-r p-1 hidden sm:table-cell">
                                <div v-if="support.emitter">
                                    {{ support.emitter.label }}
                                </div>
                            </td>
                            <td class="text-center border-r p-1 hidden sm:table-cell">
                                {{ support.emissionDate | humanizeDateShort }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </main>
</template>

<script>
import { filterMatch, sortBy } from '@/services/sanitize.service';
import { differenceInWorkingDays } from '@/services/duration.service';
import locationService, { mergeZonesPathsByFolders } from '../../services/location.service';
import { combineLatest } from 'rxjs';
import { getSupports } from './supports.service';
import IconFileDocumentOutline from '@/icons/IconFileDocumentOutline';
import IconFileImageOutline from '@/icons/IconFileImageOutline';
import { getLocationsTree } from '@/features/locations/locations.service';
import { getCalendar } from '@/features/planning/agenda/agenda.service';
import { getBundleMap } from '@/features/bundles/bundles.service';

import { humanizeDate } from '@/filters/dateFilter';
import AppSeparator from '@/components/appSeparator/AppSeparator.vue';
import { getSupportsExport } from '@/features/supports/supportsExports.service';

export default {
    components: {
        AppSeparator,
        IconFileImageOutline,
        IconFileDocumentOutline,
    },
    async created() {
        this.loading = true;
        this.subscriptions = [
            combineLatest([
                getCalendar(this.$route.params.projectId),
                getSupportsExport(this.$route.params.projectId, this.$route.params.supportsExportId),
                getSupports(this.$route.params.projectId),
                getLocationsTree(this.$route.params.projectId),
                getBundleMap(this.$route.params.projectId),
            ]).subscribe(([calendar, supportsExport, supports, folders, bundleMap]) => {
                this.calendar = calendar;
                this.restoreFilter(supportsExport);
                this.locationOptions = locationService
                    .buildLocationOptions(folders)
                    .filter((location) => location.type === 'zone');
                const locationMap = locationService.getLocationMap(folders);
                this.items = supports.map((support) => {
                    const locations = support.locationIds.map((id) => locationMap[id]);
                    return {
                        ...support,
                        filterString: [
                            support.code,
                            support.name,
                            bundleMap[support.emitterId]?.label,
                            support.emissionDate ? humanizeDate(support.emissionDate) : '',
                        ].join(),
                        locations,
                        emitter: bundleMap[support.emitterId],
                        mergedLocations: mergeZonesPathsByFolders(locations),
                    };
                });
                this.loading = false;
            }),
        ];
    },
    computed: {
        filteredItems() {
            let result = this.filterFn(this.filterValue);
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'code') {
                        return item.code;
                    } else if (this.sortKey === 'name') {
                        return item.name;
                    } else if (this.sortKey === 'emissionDueDate') {
                        return item.emissionDueDate;
                    } else if (this.sortKey === 'emissionDate') {
                        return item.emissionDate;
                    } else if (this.sortKey === 'type') {
                        return item.type;
                    } else if (this.sortKey === 'emitter') {
                        return item.emitter?.label;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
    },
    methods: {
        isDone(item) {
            return item.emissionDate;
        },
        isFar(item) {
            const diff = differenceInWorkingDays(
                item.emissionDueDate,
                new Date(),
                this.calendar,
                item.bundle ? item.bundle.id : null,
            );
            return !item.emissionDate && diff > 1;
        },
        matchBundleFilter(item, bundleIds) {
            return item.emitter && bundleIds.includes(item.emitter.id);
        },
        matchNoEmitter(item) {
            return !item.emitter;
        },
        matchNoCodeCriteria(item) {
            return !item.code || !item.code.length === 0;
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString, criteria, true));
        },
        matchStatusFilter(item) {
            const isFar = this.isFar(item);
            const isDone = this.isDone(item);
            return (!isDone && !isFar && this.coming) || (isFar && this.far) || (isDone && this.done);
        },
        restoreFilter(supportsExport) {
            this.filterValue = supportsExport.filter || [];
            this.done = !!supportsExport.showDone;
            this.coming = !!supportsExport.showComing;
            this.far = !!supportsExport.far;
            this.sortKey = supportsExport.sortKey;
            this.sortAsc = !!supportsExport.sortAsc;
        },
        filterFn(filter) {
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const locationCriteria = filter.filter((aCriteria) => aCriteria._isLocationCriteria);
            const typeCriteria = filter.filter((aCriteria) => aCriteria._isTypeCriteria).map((criteria) => criteria.id);
            const noEmitterCriteria = filter.filter((aCriteria) => aCriteria._isNoEmitterCriteria);
            const noCodeCriteria = filter.filter((aCriteria) => aCriteria._isNoCodeCriteriaCriteria);
            const bundleCriteria = filter
                .filter((aCriteria) => aCriteria._isBundleCriteria)
                .map((criteria) => criteria.id);
            return this.items.filter((item) => {
                const fullCriteria = {
                    matchStatusFilter: this.matchStatusFilter(item),
                    matchLocationCriteria: locationService.matchLocationCriteria(locationCriteria, item.locations),
                    matchString: this.matchString(stringCriteria, item),
                    matchType: this.matchType(typeCriteria, item),
                    matchBundle: bundleCriteria.length === 0 || this.matchBundleFilter(item, bundleCriteria),
                    matchNoEmitterCriteria: noEmitterCriteria.length === 0 || this.matchNoEmitter(item),
                    matchNoCodeCriteria: noCodeCriteria.length === 0 || this.matchNoCodeCriteria(item),
                };
                return Object.values(fullCriteria).every((value) => !!value);
            });
        },
        matchType(typeCriteria, item) {
            return typeCriteria.length === 0 || typeCriteria.includes(item.type);
        },
    },
    data() {
        return {
            loading: true,
            sortKey: 'name',
            sortAsc: true,
            items: [],
            calendar: [],
            done: false,
            coming: true,
            far: false,
            filterValue: [],
            subscriptions: [],
        };
    },
};
</script>
